import { Session, useAuth, ValidatedSessions } from '@ais-ui/auth';
import { LoadingSpinner } from '@ais-ui/ui-core';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { periodicCheck } from './ol-server/checkSearchAndRescue';

const SessionContext = createContext<ValidatedSessions | null>(null);

export interface SessionProviderProps {
  children: ReactNode;
  logout?: (reason: string) => void;
  reconnect?: (server: string) => void;
}

export const SessionProvider = ({
  children,
  logout,
  reconnect,
}: SessionProviderProps) => {
  const { sessions, setSessions } = useAuth();

  useEffect(() => {
    const handleSessionError = () => {
      if (logout) {
        logout('Your session has expired');
      }
    };

    window.addEventListener('sessionError', handleSessionError);
    return () => {
      window.removeEventListener('sessionError', handleSessionError);
    };
  }, [logout]);

  useEffect(() => {
    const handleConnectionClosed = async (event: Event) => {
      // Access the detail argument that tells us which session was closed
      const customEvent = event as CustomEvent<string>;
      const sessionType = customEvent?.detail;

      // We set the sessionType to undefined to indicate that the session is closed
      setSessions((prev) => ({
        ...prev,
        [sessionType]: undefined,
      }));

      if (reconnect) {
        reconnect(sessionType);
      }
    };

    window.addEventListener('connectionClosed', handleConnectionClosed);
    return () => {
      window.removeEventListener('connectionClosed', handleConnectionClosed);
    };
  }, [reconnect, setSessions]);

  // Keep-alive effect, this makes sure that the olserver does not become idle, and thus
  //  the user can turn away from their computer without getting logged out
  useEffect(() => {
    // Only set up keep-alive if we have a valid olSession
    if (!sessions?.olSession) return;

    const doPeriodicCheck = async () => {
      try {
        await periodicCheck(sessions.olSession as Session);
      } catch (error) {
        console.error('Keep-alive check failed:', error);
      }
    };

    doPeriodicCheck();

    // Set up interval for subsequent checks
    const interval = setInterval(doPeriodicCheck, 15000);

    return () => clearInterval(interval);
  }, [sessions?.olSession]);

  const value = useMemo(() => {
    if (!sessions?.olSession) return null;
    return sessions as ValidatedSessions;
  }, [sessions]);

  const content = value ? children : <LoadingSpinner />;

  return (
    <SessionContext.Provider value={value}>{content}</SessionContext.Provider>
  );
};

export const useSession = () => {
  const sessionContext = useContext(SessionContext);

  if (!sessionContext) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return sessionContext;
};
